import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import seoApi from '../../services/seoApi';

const SeoContext = createContext(null);

const upsertMeta = (attr, key, content) => {
  if (content == null) return;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const normalizePath = (path) => {
  let p = String(path || '/');
  if (!p.startsWith('/')) p = `/${p}`;
  if (p.length > 1) p = p.replace(/\/+$/, '');
  return p || '/';
};

const withSuffix = (title, suffix) => {
  const t = String(title || '').trim();
  const s = String(suffix || 'Flare Digital').trim() || 'Flare Digital';
  if (!t) return s;
  const tail = `| ${s}`;
  const lower = t.toLowerCase();
  if (lower.endsWith(tail.toLowerCase()) || lower.endsWith(s.toLowerCase())) return t;
  return `${t} ${tail}`;
};

const HOME_FALLBACK = {
  id: 'home',
  path: '/',
  index: true,
  title: "Pakistan's Best Read Magazine",
  description: 'Pakistan lifestyle, culture, and monthly Flare issues — digital download and nationwide print delivery.',
  keywords: 'flare magazine, pakistan magazine, digital flare, lifestyle magazine',
};

const PATH_FALLBACKS = {
  '/': HOME_FALLBACK,
  '/magazines': { id: 'magazines', path: '/magazines', index: true, title: 'Latest Issues', description: 'Browse current and past Flare Magazine issues. Order a digital copy or print delivery across Pakistan.', keywords: 'flare issues, magazine archive, digital magazine pakistan' },
  '/plans': { id: 'plans', path: '/plans', index: true, title: 'Subscription Plans', description: 'Monthly and annual Flare Magazine subscriptions for home delivery and digital access across Pakistan.', keywords: 'flare subscription, magazine subscription pakistan' },
  '/how': { id: 'how', path: '/how', index: true, title: 'How Flare Delivery Works', description: 'Order, subscribe, and receive Flare Magazine at home or as a digital issue — simple steps from checkout to delivery.', keywords: 'flare delivery, how flare works, magazine subscription' },
  '/contact': { id: 'contact', path: '/contact', index: true, title: 'Contact Flare Magazine', description: 'Reach Flare Magazine for subscriptions, delivery help, advertising, and support.', keywords: 'contact flare magazine, flare support' },
  '/privacy-policy': { id: 'privacy', path: '/privacy-policy', index: true, title: 'Privacy Policy', description: 'How Flare Magazine collects, uses, and protects your personal information.', keywords: 'flare privacy policy' },
  '/terms-of-service': { id: 'terms', path: '/terms-of-service', index: true, title: 'Terms of Service', description: 'Terms that govern your use of digital.flare.pk, subscriptions, and magazine orders.', keywords: 'flare terms of service' },
  '/refund-policy': { id: 'refund', path: '/refund-policy', index: true, title: 'Refund Policy', description: 'Flare refunds for stock issues and fulfilment errors. Customer order cancellation is not available. PDFs are free.', keywords: 'flare refund policy' },
  '/login': { id: 'login', path: '/login', index: true, title: 'Sign in to your account', description: 'Sign in to your Flare Magazine account to track orders, subscriptions, and deliveries.', keywords: 'flare login, magazine account' },
  '/register': { id: 'register', path: '/register', index: true, title: 'Create your Flare account', description: 'Create a Flare Magazine account to order issues, manage subscriptions, and track deliveries.', keywords: 'flare register, create account' },
  '/forgot-password': { id: 'forgot', path: '/forgot-password', index: true, title: 'Reset your password', description: 'Reset your Flare Magazine account password with a one-time email code.', keywords: 'flare password reset' },
  '/features': { id: 'features', path: '/features', index: false, canonical: '/', title: 'Features' },
  '/cart': { id: 'cart', path: '/cart', index: false, title: 'Shopping cart' },
  '/checkout': { id: 'checkout', path: '/checkout', index: false, title: 'Checkout' },
  '/verify': { id: 'verify', path: '/verify', index: false, title: 'Verify invoice' },
  '/no-access': { id: 'no_access', path: '/no-access', index: false, title: 'Access denied' },
};

const ADMIN_FALLBACK = { id: 'dashboard', path: '/dashboard', index: false, title: 'Admin' };

function matchPage(pages, path) {
  const list = pages && typeof pages === 'object' ? Object.values(pages) : [];
  const byId = (id) => list.find((p) => p.id === id) || null;
  if (path === '/features') return byId('features') || PATH_FALLBACKS['/features'];
  if (/^\/magazines\/[^/]+$/.test(path)) {
    return byId('magazine_issue') || {
      id: 'magazine_issue',
      path,
      index: true,
      title: 'Flare Magazine Issue',
      description: 'Read the latest Flare Magazine issue. Digital download or print delivery across Pakistan.',
      keywords: 'flare magazine issue, digital flare',
    };
  }
  if (/^\/order\//.test(path)) return byId('order') || { id: 'order', path, index: false, title: 'Order confirmation' };
  if (path.startsWith('/account')) return byId('account') || { id: 'account', path: '/account', index: false, title: 'My account' };

  const exact = list.find((p) => p.path && !String(p.path).includes(':') && normalizePath(p.path) === path);
  if (exact) return exact;
  if (PATH_FALLBACKS[path]) return PATH_FALLBACKS[path];

  if (path.startsWith('/portal-hd-99')) {
    const rest = normalizePath(path.slice('/portal-hd-99'.length) || '/');
    if (rest === '/' || rest === '/login') return byId('admin_login') || { id: 'admin_login', path: '/portal-hd-99/login', index: false, title: 'Admin login' };
    const inner = list.find((p) => !p.index && normalizePath(p.path) === rest);
    return inner || byId('admin_login') || ADMIN_FALLBACK;
  }

  return byId('dashboard') || ADMIN_FALLBACK;
}

export function SeoProvider({ children }) {
  const location = useLocation();
  const [payload, setPayload] = useState(null);
  const [override, setOverrideState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    seoApi.get()
      .then((res) => {
        if (!cancelled) setPayload(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setPayload(null);
      });
    return () => { cancelled = true; };
  }, []);

  const setOverride = useCallback((next) => {
    setOverrideState(next || null);
  }, []);

  const applied = useMemo(() => {
    const path = normalizePath(location.pathname);
    const suffix = payload?.suffix || 'Flare Digital';
    const origin = (typeof window !== 'undefined' ? window.location.origin : (payload?.origin || '')).replace(/\/+$/, '');
    const pages = payload?.pages || {};
    const matched = matchPage(pages, path) || HOME_FALLBACK;

    const title = override?.title || matched.title;
    const description = (override?.description && String(override.description).trim()) || matched.description || '';
    const keywords = (override?.keywords && String(override.keywords).trim()) || matched.keywords || '';
    const indexable = override?.index != null ? !!override.index : !!matched.index;
    const canonicalPath = matched.canonical && !String(matched.canonical).includes(':')
      ? matched.canonical
      : (path === '/features' ? '/' : path);

    return {
      fullTitle: withSuffix(title, suffix),
      description: indexable ? description : '',
      keywords: indexable ? keywords : '',
      robots: indexable ? 'index,follow' : 'noindex,nofollow',
      canonical: `${origin}${canonicalPath === '/' ? '/' : canonicalPath}`,
    };
  }, [location.pathname, payload, override]);

  useEffect(() => {
    document.title = applied.fullTitle;
    upsertMeta('name', 'description', applied.description);
    upsertMeta('name', 'robots', applied.robots);
    upsertMeta('name', 'keywords', applied.keywords);
    upsertMeta('property', 'og:title', applied.fullTitle);
    upsertMeta('property', 'og:description', applied.description);
    upsertLink('canonical', applied.canonical);
  }, [applied]);

  const refresh = useCallback(() => seoApi.get().then((r) => setPayload(r.data || null)), []);
  const value = useMemo(() => ({ payload, setOverride, refresh }), [payload, setOverride, refresh]);

  return <SeoContext.Provider value={value}>{children}</SeoContext.Provider>;
}

export function useSeo() {
  return useContext(SeoContext);
}

export default SeoProvider;
